import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Badge,
  InputGroup,
  Table,
  Dropdown,
} from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { useGetEventUsers } from "../../api/queries/useGetEventUsers";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useAppContext } from "../../Context/AppContext";
import { useRemoveBooking } from "../../api/mutations/useRemoveBooking";
import { LuDownload } from "react-icons/lu";
import { BsPeople, BsSearch, BsSortDown } from "react-icons/bs";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { Users } from "../../types/Types";
import { useTranslation } from "react-i18next";
import {
  getEventObjectiveDisplayLabel,
  getEventStageDisplayLabel,
} from "../../i18n/eventRegistrationI18n";
import { matchesAdminUserSearch } from "../../utils/adminUserSearch";
import DetailPageHeader from "../common/DetailPageHeader";
import "../css/Admin.css";

const ACTION_BTN_STYLE = {
  minWidth: 148,
  height: 34,
} as const;

export interface EventUser {
  // Event-specific fields
  firstName: string;
  lastName: string;
  email: string;
  stageOfBusiness: string;
  eventObjectives: string;
  sectorsOfInterest: string;
  consentToShareProfile: number;
  userId: string;
  // All User fields (optional, in case API returns them)
  id?: number;
  isAdmin?: number;
  objectId?: string;
  isGDPRConsent?: number;
  isEmailUpdates?: number;
  isAnnualMembership?: number;
  membershipId?: number;
  membershipName?: string;
  isDirectory?: number;
  isTermConditions?: number;
  hasPaid?: number;
  dateOfBirth?: string;
  idDocument?: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  gender?: string;
  languagePreference?: string;
  professionalPhoto?: string;
  aboutYou?: string;
  educationLevel?: string;
  studyInterest?: string;
  skillsExperience?: string;
  mentorshipObjectives?: string;
  mentorshipExpertise?: string;
  mentorshipFormat?: string;
  platformInterest?: string;
  peerNetworking?: string;
  productInterested?: string;
  businessName?: string;
  businessLogo?: string;
  businessSectorIndustry?: string;
  businessWebsiteUrl?: string;
  businessCountryOperation?: string;
  businessAbout?: string;
  businessAddressLineOne?: string;
  businessAddressLineTwo?: string;
  businessCountry?: string;
  businessPostCode?: string;
  businessTelephoneNumber?: string;
  businessCurrency?: string;
  businessTurnover?: string;
  businessObjectives?: string;
  businessProductServices?: string;
  businessDiscount?: string;
  businessDiscountCode?: string;
  personalGender?: string;
  personalLanguagePreference?: string;
  personalAbout?: string;
  personalProfessionalPhoto?: string;
  personalPositionCompany?: string;
  hasCompletedProfile?: number;
  county?: string;
  IdDocument?: string;
  PersonalProfessionalPhoto?: string;
  BusinessLogo?: string;
  ProfessionalPhoto?: string;
  DiscountCode?: string;
  DiscountEndDate?: string;
  bannerImage?: string;
  BannerImage?: string;
  universityCollege?: string;
  referralCode?: string;
  referralCodeUsed?: string;
  lastLoggedIn?: string;
  isWelcomeEmailSent?: number;
  createdDate?: string;
}

const EventDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setViewMode } = useAppContext();
  const { data: EventUsers, refetch: fetchEventUsers } = useGetEventUsers({
    id: id || "",
  });
  const { data: eventsData } = useGetEvent({ eventId: id || "" });
  const [selectedUser, setSelectedUser] = useState<EventUser | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("connections.sort.recent");
  const [cancelModal, setCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  const { mutate: removeBooking } = useRemoveBooking({
    onSuccess: () => {
      setCancelModal(false);
      setSelectedUser(null);
      setShowRefundModal(true);
      fetchEventUsers();
    },
  });

  const handleCancelBooking = () => {
    if (selectedUser) {
      removeBooking({
        userId: selectedUser.objectId || selectedUser.userId,
        eventId: parseInt(id || "0"),
      });
    }
  };

  useEffect(() => {
    if (user?.user?.isAdmin !== 1) {
      navigate("/");
      return;
    }
    setViewMode("admin");
  }, [user, navigate, setViewMode]);

  const handleUserClick = (user: EventUser) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const filteredUsers: EventUser[] = useMemo(() => {
    const list =
      (EventUsers?.users as EventUser[])?.filter((user) =>
        matchesAdminUserSearch(user, searchTerm),
      ) || [];

    const sorted = [...list];
    if (sortOption === "connections.sort.firstName") {
      sorted.sort((a, b) =>
        (a.firstName || "").localeCompare(b.firstName || "", undefined, {
          sensitivity: "base",
        }),
      );
    } else if (sortOption === "connections.sort.lastName") {
      sorted.sort((a, b) =>
        (a.lastName || "").localeCompare(b.lastName || "", undefined, {
          sensitivity: "base",
        }),
      );
    } else {
      sorted.sort((a, b) => {
        const aDate = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const bDate = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return bDate - aDate;
      });
    }
    return sorted;
  }, [EventUsers?.users, searchTerm, sortOption]);

  const escapeCSV = (value: string | number | undefined | null): string => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCSV = async () => {
    if (!filteredUsers.length) return;

    setIsDownloadingCSV(true);
    try {
      // Fetch full user data for each user
      const usersWithFullData: EventUser[] = await Promise.all(
        filteredUsers.map(async (eventUser) => {
          try {
            // Fetch full user profile using userId as objectId
            const response = await axiosInstance<Users>(
              getAPIURL(
                `/User/ByObjectId/${eventUser.userId}?id=${eventUser.userId}`
              )
            );
            const fullUserData = response.data?.user;
            const imageUrls = response.data?.imageUrls;

            // Merge event-specific data with full user data
            return {
              ...eventUser,
              ...fullUserData,
              ...imageUrls,
              // Ensure event-specific fields are preserved
              stageOfBusiness: eventUser.stageOfBusiness,
              eventObjectives: eventUser.eventObjectives,
              sectorsOfInterest: eventUser.sectorsOfInterest,
              consentToShareProfile: eventUser.consentToShareProfile,
            } as EventUser;
          } catch (error) {
            console.error(
              `Error fetching user data for ${eventUser.userId}:`,
              error
            );
            // Return event user data only if fetch fails
            return eventUser;
          }
        })
      );

      const csvRows = [];

      csvRows.push(`Event: ${eventsData?.name}`);

      csvRows.push("");

      // Define all possible user fields in order (excluding IDs, boolean 0/1 fields, and createdDate)
      const fieldMappings: Array<{
        key: string;
        label: string;
        getValue?: (user: any) => any;
      }> = [
        // Basic Info
        { key: "firstName", label: "First Name" },
        { key: "lastName", label: "Last Name" },
        { key: "email", label: "Email" },
        { key: "phoneNumber", label: "Phone Number" },
        { key: "dateOfBirth", label: "Date of Birth" },
        { key: "gender", label: "Gender" },
        { key: "personalGender", label: "Personal Gender" },

        // Location
        { key: "city", label: "City" },
        { key: "county", label: "County" },
        { key: "country", label: "Country" },

        // Personal Profile
        { key: "aboutYou", label: "About You" },
        { key: "personalAbout", label: "Personal About" },
        { key: "languagePreference", label: "Language Preference" },
        {
          key: "personalLanguagePreference",
          label: "Personal Language Preference",
        },
        { key: "personalPositionCompany", label: "Position/Company" },
        {
          key: "PersonalProfessionalPhoto",
          label: "Personal Professional Photo URL",
        },
        { key: "ProfessionalPhoto", label: "Professional Photo URL" },
        { key: "IdDocument", label: "ID Document URL" },

        // Education
        { key: "educationLevel", label: "Education Level" },
        { key: "studyInterest", label: "Study Interest" },
        { key: "universityCollege", label: "University/College" },

        // Skills & Experience
        { key: "skillsExperience", label: "Skills & Experience" },

        // Mentorship
        { key: "mentorshipObjectives", label: "Mentorship Objectives" },
        { key: "mentorshipExpertise", label: "Mentorship Expertise" },
        { key: "mentorshipFormat", label: "Mentorship Format" },

        // Platform & Networking
        { key: "platformInterest", label: "Platform Interest" },
        { key: "peerNetworking", label: "Peer Networking" },
        { key: "productInterested", label: "Product Interested" },

        // Business Information
        { key: "businessName", label: "Business Name" },
        { key: "businessSectorIndustry", label: "Business Sector/Industry" },
        { key: "businessWebsiteUrl", label: "Business Website URL" },
        { key: "businessAbout", label: "Business About" },
        { key: "businessAddressLineOne", label: "Business Address Line 1" },
        { key: "businessAddressLineTwo", label: "Business Address Line 2" },
        { key: "businessCountry", label: "Business Country" },
        {
          key: "businessCountryOperation",
          label: "Business Country Operation",
        },
        { key: "businessPostCode", label: "Business Post Code" },
        { key: "businessTelephoneNumber", label: "Business Telephone" },
        { key: "businessCurrency", label: "Business Currency" },
        { key: "businessTurnover", label: "Business Turnover" },
        { key: "businessObjectives", label: "Business Objectives" },
        { key: "businessProductServices", label: "Business Products/Services" },
        { key: "businessDiscount", label: "Business Discount" },
        { key: "businessDiscountCode", label: "Business Discount Code" },
        { key: "BusinessLogo", label: "Business Logo URL" },

        // Event-specific fields
        { key: "stageOfBusiness", label: "Stage of Business" },
        { key: "eventObjectives", label: "Event Objectives" },
        { key: "sectorsOfInterest", label: "Sectors of Interest" },

        // Membership
        { key: "membershipName", label: "Membership Name" },
        {
          key: "membershipType",
          label: "Membership Type",
          getValue: (user: any) => {
            if (user.isAnnualMembership === 1) return "Annual";
            if (user.isAnnualMembership === 0) return "Monthly";
            return "";
          },
        },

        // Discounts & Referrals
        { key: "DiscountCode", label: "Discount Code" },
        { key: "DiscountEndDate", label: "Discount End Date" },
        { key: "referralCode", label: "Referral Code" },
        { key: "referralCodeUsed", label: "Referral Code Used" },

        // Images
        { key: "BannerImage", label: "Banner Image URL" },

        // Activity
        { key: "lastLoggedIn", label: "Last Logged In" },
      ];

      // Build CSV header
      const headers = fieldMappings.map((field) => field.label);
      csvRows.push(headers.join(","));

      // Build CSV rows with full user data
      usersWithFullData.forEach((user) => {
        const row = fieldMappings.map((field) => {
          let value;
          if (field.getValue) {
            value = field.getValue(user);
          } else {
            value = (user as any)[field.key];
          }
          return escapeCSV(value);
        });
        csvRows.push(row.join(","));
      });

      const csvData = new Blob([csvRows.join("\n")], { type: "text/csv" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(csvData);
      link.download = `${eventsData?.name}_users.csv`;
      link.click();
    } catch (error) {
      console.error("Error generating CSV:", error);
      alert(t("admin.eventDetails.csvFailed"));
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const handleBack = () => {
    navigate("/admin", { state: { adminTab: "events" } });
  };

  const openUserProfile = (eventUser: EventUser) => {
    const objectId = eventUser.objectId || eventUser.userId;
    if (!objectId) return;
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate(`/user-profile/${objectId}`, {
      state: {
        from,
        adminTab: "events",
      },
    });
  };

  return (
    <main className="page-content w-100 pb-4 m-0">
      <DetailPageHeader
        onBack={handleBack}
        eyebrow={t("sidebar.events")}
        title={eventsData?.name || t("admin.eventDetails.bookedUsers")}
        meta={
          <>
            <BsPeople size={14} aria-hidden />
            <span>
              {t("admin.eventDetails.bookedUsers")} ({filteredUsers.length})
            </span>
          </>
        }
        actions={
          <div className="detail-page-header__toolbar d-flex flex-column gap-2 w-100">
            <div className="d-flex align-items-center gap-2 w-100 min-w-0">
              <InputGroup className="flex-grow-1 min-w-0 bg-white rounded border overflow-hidden">
                <InputGroup.Text className="bg-white border-0 text-secondary">
                  <BsSearch size={16} aria-hidden />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("admin.eventDetails.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-white border-0 shadow-none text-dark"
                />
              </InputGroup>
              <Dropdown onSelect={(k) => k && setSortOption(k)}>
                <Dropdown.Toggle
                  variant="outline-light"
                  className="shadow-none d-flex align-items-center gap-2 flex-shrink-0 text-nowrap"
                >
                  <BsSortDown size={16} aria-hidden />
                  <span>{t("connections.sort")}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item eventKey="connections.sort.recent">
                    {t("connections.sort.recent")}
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="connections.sort.firstName">
                    {t("connections.sort.firstName")}
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="connections.sort.lastName">
                    {t("connections.sort.lastName")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <Button
              onClick={downloadCSV}
              variant="outline-light"
              className="d-inline-flex gap-2 align-items-center justify-content-center shadow-none align-self-end px-3 py-1"
              size="sm"
              disabled={isDownloadingCSV || !filteredUsers.length}
            >
              {isDownloadingCSV ? (
                <>
                  <Spinner size="sm" animation="border" />
                  {t("admin.eventDetails.downloading")}
                </>
              ) : (
                <>
                  {t("admin.eventDetails.downloadUsersList")}
                  <LuDownload aria-hidden />
                </>
              )}
            </Button>
          </div>
        }
        actionsClassName="d-none d-md-block"
      />

      <div className="d-md-none mb-3">
        <div className="d-flex justify-content-end mb-2">
          <Button
            onClick={downloadCSV}
            variant="outline-primary"
            className="d-inline-flex gap-2 align-items-center justify-content-center shadow-none px-3 py-1"
            size="sm"
            disabled={isDownloadingCSV || !filteredUsers.length}
          >
            {isDownloadingCSV ? (
              <>
                <Spinner size="sm" animation="border" />
                {t("admin.eventDetails.downloading")}
              </>
            ) : (
              <>
                {t("admin.eventDetails.downloadUsersList")}
                <LuDownload aria-hidden />
              </>
            )}
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <InputGroup className="rounded-2 flex-grow-1 min-w-0">
            <InputGroup.Text className="bg-white border-end-0 rounded-start">
              <BsSearch size={16} className="text-secondary" aria-hidden />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t("admin.eventDetails.searchUsers")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-start-0 rounded-end bg-white"
            />
          </InputGroup>
          <Dropdown onSelect={(k) => k && setSortOption(k)}>
            <Dropdown.Toggle
              variant="outline-primary"
              className="rounded-2 shadow-none d-flex align-items-center gap-2 flex-shrink-0"
            >
              <BsSortDown size={16} aria-hidden />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item eventKey="connections.sort.recent">
                {t("connections.sort.recent")}
              </Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.firstName">
                {t("connections.sort.firstName")}
              </Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.lastName">
                {t("connections.sort.lastName")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <h6 className="fw-bold mb-0 text-dark">
          {t("admin.eventDetails.bookedUsers")} ({filteredUsers.length})
        </h6>
      </div>

      <div className="admin-data-table-wrap bg-white overflow-hidden">
        <Table hover responsive className="admin-data-table align-middle mb-0">
          <thead>
            <tr>
              <th className="fw-semibold text-uppercase text-nowrap align-middle">
                {t("admin.eventDetails.name")}
              </th>
              <th className="fw-semibold text-uppercase text-nowrap align-middle">
                {t("admin.eventDetails.email")}
              </th>
              <th className="fw-semibold text-uppercase text-nowrap align-middle">
                {t("events.eventObjectives")}
              </th>
              <th className="fw-semibold text-uppercase text-nowrap align-middle">
                {t("events.stageOfBusiness")}
              </th>
              <th className="fw-semibold text-uppercase text-nowrap align-middle text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  {t("connections.noUsersFound")}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user, idx) => {
                const fullName =
                  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                  "—";
                const objectiveLabel = user.eventObjectives
                  ? getEventObjectiveDisplayLabel(user.eventObjectives, t)
                  : "";
                const stageLabel = user.stageOfBusiness
                  ? getEventStageDisplayLabel(user.stageOfBusiness, t)
                  : "";

                return (
                  <tr key={user.userId || user.objectId || idx}>
                    <td className="align-middle" style={{ maxWidth: 220 }}>
                      <button
                        type="button"
                        className="btn btn-link events-table-name-link p-0 border-0 shadow-none text-decoration-none text-start w-100 overflow-hidden"
                        onClick={() => handleUserClick(user)}
                        title={fullName}
                      >
                        <span className="fw-semibold text-truncate d-block lh-sm min-w-0">
                          {fullName}
                        </span>
                      </button>
                    </td>
                    <td
                      className="admin-table-muted align-middle"
                      style={{ maxWidth: 240 }}
                    >
                      <span
                        className="text-truncate d-block"
                        title={user.email || undefined}
                      >
                        {user.email || "—"}
                      </span>
                    </td>
                    <td className="align-middle" style={{ maxWidth: 220 }}>
                      {objectiveLabel ? (
                        <span
                          className="text-truncate d-block"
                          title={objectiveLabel}
                        >
                          {objectiveLabel}
                        </span>
                      ) : (
                        <span className="admin-table-muted">—</span>
                      )}
                    </td>
                    <td className="align-middle" style={{ maxWidth: 180 }}>
                      {stageLabel ? (
                        <Badge
                          bg="primary-subtle"
                          text="primary-emphasis"
                          pill
                          className="fw-semibold"
                        >
                          {stageLabel}
                        </Badge>
                      ) : (
                        <span className="admin-table-muted">—</span>
                      )}
                    </td>
                    <td className="align-middle text-end text-nowrap">
                      <div className="d-inline-flex flex-wrap gap-2 justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="rounded-2 d-inline-flex align-items-center justify-content-center"
                          style={ACTION_BTN_STYLE}
                          onClick={() => openUserProfile(user)}
                        >
                          {t("connections.viewProfile")}
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-2 d-inline-flex align-items-center justify-content-center"
                          style={ACTION_BTN_STYLE}
                          onClick={() => {
                            setSelectedUser(user);
                            setCancelModal(true);
                          }}
                        >
                          {t("admin.eventDetails.cancelBooking")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal show={cancelModal} centered onHide={() => setCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.cancelBooking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("admin.eventDetails.confirmCancelFor")}{" "}
          {selectedUser?.firstName} {selectedUser?.lastName}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCancelModal(false)}>
            {t("admin.eventDetails.noKeepBooking")}
          </Button>
          <Button variant="danger" onClick={handleCancelBooking}>
            {t("admin.eventDetails.yesCancelBooking")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.userDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                <strong>{t("admin.eventDetails.name")}:</strong>{" "}
                {selectedUser.firstName} {selectedUser.lastName}
              </p>
              <p>
                <strong>{t("admin.eventDetails.email")}:</strong>{" "}
                {selectedUser.email}
              </p>
              <p>
                <strong>{t("events.eventObjectives")}:</strong>{" "}
                {getEventObjectiveDisplayLabel(selectedUser.eventObjectives, t)}
              </p>
              <p>
                <strong>{t("events.stageOfBusiness")}:</strong>{" "}
                {getEventStageDisplayLabel(selectedUser.stageOfBusiness, t)}
              </p>
              <p className="mb-0">
                <strong>{t("events.sectorsOfInterest")}:</strong>{" "}
                {selectedUser.sectorsOfInterest}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => {
              if (selectedUser) openUserProfile(selectedUser);
            }}
          >
            {t("connections.viewProfile")}
          </Button>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.bookingCancelled")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            <strong>{t("admin.eventDetails.important")}:</strong>{" "}
            {t("admin.eventDetails.refundManual")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowRefundModal(false)}>
            {t("common.done")}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default EventDetails;
